import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  CreditCard,
  ArrowUpRight,
  Users,
  HelpCircle,
  Coins,
  Calendar,
  MessageCircle,
  LogIn,
  CandlestickChart,
  PiggyBank,
  Download,
  Upload
} from 'lucide-react'
import { apiService } from '@/services/api'
import { toast } from 'react-hot-toast'

interface DashboardStats {
  pending_deposits: number
  today_deposits: number
  pending_withdrawals: number
  today_withdrawals: number
  new_inquiries: number
  today_signups: number
  total_users: number
  pending_registrations: number
  active_stakings: number
  event_participants: number
  pending_chats: number
  today_traders: number
  today_stakers: number
  today_visitors: number
  today_depositors: number
  today_withdrawers: number
  updated_at: string
}

const StatsCards = () => {
  const { t } = useTranslation(['dashboard', 'common'])
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchDashboardStats = async () => {
    try {
      const client = apiService.getClient()
      const response = await client.get('/api/v1/admin/dashboard/stats')

      if (response.data?.result === 'success') {
        setStats(response.data.data)
      } else {
        toast.error(t('dashboard:stats.loadFailed'))
      }
    } catch (error) {
      console.error('Dashboard stats load failed:', error)
      toast.error(t('dashboard:stats.loadFailedDetail'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardStats()

    // 5분마다 데이터 새로고침
    const interval = setInterval(fetchDashboardStats, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="glass-card p-4 sm:p-6 animate-pulse">
            <div className="h-6 bg-dark-600 rounded mb-4"></div>
            <div className="h-8 bg-dark-600 rounded mb-2"></div>
            <div className="h-4 bg-dark-600 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="glass-card p-6 text-center">
        <p className="text-dark-400">{t('dashboard:stats.noData')}</p>
      </div>
    )
  }

  const statsData = [
    {
      title: t('dashboard:stats.pendingDeposit'),
      value: stats.pending_deposits,
      icon: CreditCard,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      onClick: () => navigate('/admin/deposits-withdrawals-management')
    },
    {
      title: t('dashboard:stats.pendingWithdrawal'),
      value: stats.pending_withdrawals,
      icon: ArrowUpRight,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      onClick: () => navigate('/admin/deposits-withdrawals-management')
    },
    {
      title: t('dashboard:stats.pendingApproval'),
      value: stats.pending_registrations,
      icon: Users,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      onClick: () => navigate('/admin/users?approval_status=PENDING')
    },
    {
      title: t('dashboard:stats.newInquiries'),
      value: stats.new_inquiries,
      icon: HelpCircle,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      onClick: () => navigate('/admin/inquiries')
    },
    {
      title: t('dashboard:stats.staking'),
      value: stats.active_stakings,
      icon: Coins,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      onClick: () => navigate('/admin/staking')
    },
    {
      title: t('dashboard:stats.eventParticipation'),
      value: stats.event_participants,
      icon: Calendar,
      color: 'text-indigo-400',
      bgColor: 'bg-indigo-500/10',
      onClick: () => navigate('/admin/events')
    },
    {
      title: t('dashboard:stats.newChat'),
      value: stats.pending_chats,
      icon: MessageCircle,
      color: 'text-pink-400',
      bgColor: 'bg-pink-500/10',
      onClick: () => navigate('/admin/chat')
    },
    // 오늘 활동 고유 회원 (KST 00:00~23:59:59, 테스트 계정 제외)
    {
      title: t('dashboard:stats.todayVisitors'),
      value: stats.today_visitors ?? 0,
      icon: LogIn,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10',
      onClick: () => navigate('/admin/access-logs')
    },
    {
      title: t('dashboard:stats.todayTraders'),
      value: stats.today_traders ?? 0,
      icon: CandlestickChart,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      onClick: () => navigate('/admin/positions')
    },
    {
      title: t('dashboard:stats.todayStakers'),
      value: stats.today_stakers ?? 0,
      icon: PiggyBank,
      color: 'text-teal-400',
      bgColor: 'bg-teal-500/10',
      onClick: () => navigate('/admin/staking')
    },
    {
      title: t('dashboard:stats.todayDepositors'),
      value: stats.today_depositors ?? 0,
      icon: Download,
      color: 'text-lime-400',
      bgColor: 'bg-lime-500/10',
      onClick: () => navigate('/admin/deposits-withdrawals-management')
    },
    {
      title: t('dashboard:stats.todayWithdrawers'),
      value: stats.today_withdrawers ?? 0,
      icon: Upload,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      onClick: () => navigate('/admin/deposits-withdrawals-management')
    },
  ]
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-2 sm:gap-3">
      {statsData.map((stat, index) => (
        <motion.div
          key={stat.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          onClick={stat.onClick}
          className="glass-card p-2 sm:p-3 hover:bg-white/10 transition-all duration-200 group cursor-pointer"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-1.5">
            <div className={`p-1 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`w-3.5 h-3.5 ${stat.color}`} />
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-[9px] sm:text-xs font-medium text-dark-400 group-hover:text-dark-300 transition-colors mb-0.5 sm:mb-1 truncate">
              {stat.title}
            </h3>

            <p className="text-sm sm:text-lg font-bold text-white">
              {stat.value.toLocaleString()}
            </p>
          </div>
        </motion.div>
      ))}
    </div>
  )
}

export default StatsCards
